import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { DEFAULT_PAGE_SIZE } from '@catalyst-psa/shared';

import { useAuth } from '../../auth/authContext.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { DataState } from '../../components/ui/DataState.js';
import { Pagination } from '../../components/ui/Pagination.js';
import { CustomerSelectField } from '../customers/CustomerSelectField.js';
import { QboSyncLogPanel } from '../qbo/QboSyncLogPanel.js';
import { useInvoices, useQboBatch } from './api.js';
import { INVOICE_SOURCE_LABELS, INVOICE_STATUS_LABELS, INVOICE_STATUS_TONES } from './status.js';

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

const RESULT_TONES = {
  synced: 'green',
  dry_run: 'amber',
  failed: 'red',
  pending: 'gray',
} as const;

const RESULT_LABELS = {
  synced: 'Sent to QBO',
  dry_run: 'Dry run',
  failed: 'Failed',
  pending: 'Pending',
} as const;

export function QboBatchPage(): JSX.Element {
  const { can } = useAuth();
  const canSend = can('invoices', 'edit');

  const [page, setPage] = useState(1);
  const [customerId, setCustomerId] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [testEmail, setTestEmail] = useState(false);

  const query = useInvoices({
    page,
    pageSize: DEFAULT_PAGE_SIZE,
    statuses: ['draft', 'in_review'],
    customerId: customerId || undefined,
  });
  const batch = useQboBatch();

  // Credit memos cannot be pushed to QuickBooks as invoices; hide them from the batch.
  const invoices = (query.data?.data ?? []).filter((i) => i.documentType !== 'credit_memo');
  const pageInfo = query.data?.page;

  const visibleIds = useMemo(() => invoices.map((i) => i.id), [invoices]);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));

  function toggle(id: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAllVisible(): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        visibleIds.forEach((id) => next.delete(id));
      } else {
        visibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }

  function run(email: boolean): void {
    const invoiceIds = [...selected];
    if (invoiceIds.length === 0) return;
    batch.mutate(
      { invoiceIds, email, testEmail: email ? testEmail : false },
      { onSuccess: () => setSelected(new Set()) },
    );
  }

  const selectedCount = selected.size;
  const busy = batch.isPending;
  const results = batch.data?.items ?? [];

  return (
    <div>
      <PageHeader
        title="Send to QuickBooks"
        description="Push draft and in-review invoices to QuickBooks Online, optionally emailing each one to the customer's billing contacts."
      />

      <div className="mb-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <CustomerSelectField
            value={customerId}
            onChange={(value) => {
              setPage(1);
              setCustomerId(value);
            }}
            placeholder="All customers"
            allowClear
          />
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <label className="inline-flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={testEmail}
              onChange={(e) => setTestEmail(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
            />
            Test email (send only to invoices@nscatalyst.com)
          </label>
          <div className="flex-1" />
          <span className="text-sm text-slate-500">{selectedCount} selected</span>
          <Button
            variant="secondary"
            onClick={() => run(false)}
            disabled={!canSend || selectedCount === 0 || busy}
            isLoading={busy}
          >
            Send to QBO
          </Button>
          <Button
            onClick={() => run(true)}
            disabled={!canSend || selectedCount === 0 || busy}
            isLoading={busy}
          >
            Email and Send to QBO
          </Button>
        </div>

        {batch.isError ? (
          <p className="mt-3 text-sm text-rose-600">
            {(batch.error as Error)?.message ?? 'The batch could not be started.'}
          </p>
        ) : null}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3 font-medium">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleAllVisible}
                  aria-label="Select all"
                  className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                />
              </th>
              <th className="px-4 py-3 font-medium">Number</th>
              <th className="px-4 py-3 font-medium">Customer</th>
              <th className="px-4 py-3 font-medium">Kind</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 text-right font-medium">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <DataState
              colSpan={6}
              isLoading={query.isLoading}
              isError={query.isError}
              error={query.error}
              onRetry={() => void query.refetch()}
              isEmpty={invoices.length === 0}
              emptyTitle="No invoices to send"
              emptyDescription="There are no draft or in-review invoices for this filter."
            >
              {invoices.map((invoice) => (
                <tr
                  key={invoice.id}
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => toggle(invoice.id)}
                >
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selected.has(invoice.id)}
                      onChange={() => toggle(invoice.id)}
                      aria-label={`Select ${invoice.number}`}
                      className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                    />
                  </td>
                  <td className="px-4 py-3 font-medium text-slate-900">{invoice.number}</td>
                  <td className="px-4 py-3 text-slate-600">{invoice.customerName ?? '—'}</td>
                  <td className="px-4 py-3 text-slate-600">
                    {INVOICE_SOURCE_LABELS[invoice.source]}
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={INVOICE_STATUS_TONES[invoice.status]}>
                      {INVOICE_STATUS_LABELS[invoice.status]}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-slate-900">
                    {currency.format(Number(invoice.total))}
                  </td>
                </tr>
              ))}
            </DataState>
          </tbody>
        </table>

        {pageInfo && invoices.length > 0 ? (
          <Pagination
            page={pageInfo.page}
            pageSize={pageInfo.pageSize}
            totalItems={pageInfo.totalItems}
            totalPages={pageInfo.totalPages}
            onPageChange={setPage}
          />
        ) : null}
      </div>

      {results.length > 0 ? (
        <div className="mt-6 rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 px-4 py-3 text-sm font-medium text-slate-700">
            Results
          </div>
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3 font-medium">Number</th>
                <th className="px-4 py-3 font-medium">Customer</th>
                <th className="px-4 py-3 font-medium">QBO</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Detail</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {results.map((item) => (
                <tr key={item.invoiceId}>
                  <td className="px-4 py-3 font-medium">
                    <Link
                      to={`/invoices/${item.invoiceId}`}
                      className="text-brand-600 hover:underline"
                    >
                      {item.number}
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <Link
                      to={`/invoices/${item.invoiceId}`}
                      className="align-middle text-brand-600 hover:underline"
                    >
                      {item.customerName ?? '—'}
                    </Link>
                    {item.customerCreated ? (
                      <span className="ml-2 inline-flex align-middle">
                        <Badge tone="blue">Created in QBO</Badge>
                      </span>
                    ) : null}
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={RESULT_TONES[item.status]}>{RESULT_LABELS[item.status]}</Badge>
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {item.emailed ? (
                      `Sent to ${item.emailRecipients.join(', ')}`
                    ) : item.emailNote ? (
                      <span className="text-amber-600">{item.emailNote}</span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {item.error ? (
                      <span className="text-rose-600">{item.error}</span>
                    ) : item.qboInvoiceUrl ? (
                      <a
                        href={item.qboInvoiceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-brand-600 hover:underline"
                      >
                        Open in QBO ↗
                      </a>
                    ) : item.qboInvoiceId ? (
                      `QBO #${item.qboInvoiceId}`
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <div className="mt-6">
        <QboSyncLogPanel title="QBO sync log" />
      </div>
    </div>
  );
}
